import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Field, TextInput, TextArea, PrimaryButton } from "@/components/admin/ui";

export default function ManageSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const save = useMutation({
    mutationFn: async () => api.put("/admin/settings", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });

  if (!form) return <p className="text-slate-500">Loading...</p>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-white">Website Settings</h1>
      <p className="mt-1 text-slate-400">Manage your brand, contact info and links.</p>

      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="mt-8 max-w-2xl space-y-6">
        <div className="card-surface space-y-4 rounded-2xl p-6">
          <h2 className="font-bold text-gold">Brand</h2>
          <ImageUpload value={form.logo_url} onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))} label="Logo" testid="settings-logo" />
          <Field label="Site Name"><TextInput value={form.site_name || ""} onChange={set("site_name")} testid="settings-sitename" /></Field>
          <Field label="Main Website Title"><TextInput value={form.site_title || ""} onChange={set("site_title")} testid="settings-title" /></Field>
          <Field label="Description / Tagline"><TextArea value={form.description || ""} onChange={set("description")} testid="settings-description" /></Field>
        </div>

        <div className="card-surface space-y-4 rounded-2xl p-6">
          <h2 className="font-bold text-gold">Links & Contact</h2>
          <Field label="App Download Link"><TextInput value={form.app_download_link || ""} onChange={set("app_download_link")} placeholder="https://play.google.com/..." testid="settings-applink" /></Field>
          <Field label="Support WhatsApp (full number with country code, digits only)"><TextInput value={form.support_whatsapp || ""} onChange={(e) => setForm((f) => ({ ...f, support_whatsapp: e.target.value.replace(/\D/g, "") }))} placeholder="8801700000000" testid="settings-support" /></Field>
          <Field label="Default WhatsApp Message"><TextInput value={form.whatsapp_message || ""} onChange={set("whatsapp_message")} testid="settings-message" /></Field>
          <Field label="Contact Info"><TextInput value={form.contact_info || ""} onChange={set("contact_info")} placeholder="support@goldenlive.com" testid="settings-contact" /></Field>
          <Field label="Footer Text"><TextArea value={form.footer_text || ""} onChange={set("footer_text")} testid="settings-footer" /></Field>
        </div>

        <div className="card-surface space-y-4 rounded-2xl p-6">
          <h2 className="font-bold text-gold">Social Links (optional)</h2>
          <Field label="Facebook"><TextInput value={form.social_facebook || ""} onChange={set("social_facebook")} testid="settings-facebook" /></Field>
          <Field label="Instagram"><TextInput value={form.social_instagram || ""} onChange={set("social_instagram")} testid="settings-instagram" /></Field>
          <Field label="YouTube"><TextInput value={form.social_youtube || ""} onChange={set("social_youtube")} testid="settings-youtube" /></Field>
          <Field label="Telegram"><TextInput value={form.social_telegram || ""} onChange={set("social_telegram")} testid="settings-telegram" /></Field>
        </div>

        <PrimaryButton type="submit" disabled={save.isPending} testid="save-settings-btn"><Save className="h-5 w-5" /> {save.isPending ? "Saving..." : "Save Settings"}</PrimaryButton>
      </form>
    </div>
  );
}
